// Posting — lists-at-scale mock history (00-ds-09 §9, D148). Entirely
// client-side: the Posted tab pages, searches and filters over these rows. Real
// data comes from the server index (§8); this only exists so the demo can page,
// search and filter at scale.
//
// Since rcm-32 the recent rows are built from the same deposits the Matched tab
// shows, so "Open it in Posted" always finds the payment, and the Posted
// month count (38) comes from this list rather than a typed-in number. Card
// payments (never matched to the bank, D195) live here only.

import { generate, pick, int, money as randMoney, fullName, rng } from '@/components/lists';
import { addDays, isoFromLabel, longDate, POSTING_TODAY_ISO } from './dates';
import {
  buildBaseDeposits,
  buildFreshUploadDeposits,
  buildNewAccountDeposits,
  DM_POSTED,
} from './matchedMock';
import type { CardPayer, MatchedDeposit } from './types';
import { CARD_FEE_RATE } from './cardFee';
import { creditSeeds, type CreditState } from './creditMock';
import { referenceText } from './reference';

/** The office went live ~18 months before the mock "now" (Apr 2025). */
export const GO_LIVE_ISO = '2025-04-01';

/** The extra §3 filter this page adds: "kind of payment". */
export const POSTED_KINDS = [
  'Insurance payment',
  'Secondary payment',
  'Takeback',
  'Interest',
  'Refund',
] as const;
export type PostedKind = (typeof POSTED_KINDS)[number];

const CONFIRMERS = ['Ava Thompson (admin)', 'Ryan Cole', 'Maria Flores', 'Dev Patel', 'Autopilot'] as const;
const OLD_PAYERS = [
  'Aetna',
  'Delta Dental of Texas',
  'Cigna',
  'MetLife',
  'UnitedHealthcare',
  'Guardian',
  'Sun Life',
  'BCBS of Texas',
  'United Concordia',
] as const;

export type PostedMethod = 'eft' | 'card' | 'check';

export interface PostedHistoryRow {
  id: string;
  patient: string;
  payer: string;
  /** signed: takebacks are negative. */
  amount: number;
  kind: PostedKind;
  /** Day it was posted in Dentrix. */
  dateIso: string;
  dateLabel: string;
  trace: string;
  claimNumber: string;
  confirmedBy: string;
  /** How the payer paid: card payments settle through the card processor (D195). */
  method: PostedMethod;
  /** The card processing cost (D283): shown on its own line; the payment itself is always the full amount. */
  fee?: number;
  /** Card: the approval code the card machine gave ("Charged · approval 044921 · settlement not checked"). */
  approval?: string;
  last4?: string;
  /** D271: someone posted it in the PMS before ELVA did; ELVA linked it and didn't post. */
  state?: 'already_posted';
  checkNo?: string;
  /** "Guardian check 77134406" — the reference written to the PMS (D199). */
  reference?: string;
  /** H1 §5: this payment left a patient credit, and where the credit stands now. */
  credit?: { state: CreditState; stateIso?: string; sinceIso: string; pronoun: 'he' | 'she' };
  /** D197: the payer paid the patient. */
  paidToPatient?: boolean;
  /** A line under the amount ("MetLife sent $580.00 to Iris"). */
  note?: string;
}

function hash(s: string): number {
  let n = 2166136261;
  for (let i = 0; i < s.length; i++) {
    n ^= s.charCodeAt(i);
    n = Math.imul(n, 16777619);
  }
  return n >>> 0;
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ---------------------------------------------------------------------------
// card payments (never matched to the bank)
// ---------------------------------------------------------------------------

export { CARD_FEE_RATE };

/** The fee line under a card payment, printed exactly as the spec has it (D283). Never reduces the payment. */
export function CARD_FEE_LABEL(fee: number): string {
  return `Card fee · 3% · $${fee.toFixed(2)} · a processing cost, not on the patient’s account`;
}


const CARD_SEED: { payer: string; iso: string; amount: number; patient: string }[] = [
  { payer: 'Sun Life', iso: '2026-07-28', amount: 650.67, patient: 'Marcus Webb' },
  { payer: 'Sun Life', iso: '2026-08-25', amount: 653.33, patient: 'Elena Brandt' },
  { payer: 'Sun Life', iso: '2026-09-08', amount: 653.33, patient: 'Darius Holt' },
  { payer: 'Sun Life', iso: '2026-09-29', amount: 656.0, patient: 'Nadia Quinn' },
  { payer: 'Humana', iso: '2026-08-31', amount: 412.0, patient: 'Felix Moran' },
  { payer: 'Humana', iso: '2026-09-22', amount: 390.0, patient: 'Greta Lund' },
  { payer: 'Humana', iso: '2026-10-06', amount: 520.0, patient: 'Hugo Salas' },
  { payer: 'Principal', iso: '2026-09-15', amount: 284.0, patient: 'Wendy Frost' },
  { payer: 'GEHA', iso: '2026-09-03', amount: 345.0, patient: 'Imran Dale' },
];

/** The payer asked to switch before the demo starts: Humana, Sep 2. */
export const INITIAL_CARD_DISMISSALS: Record<string, string> = { Humana: '2026-09-02' };

/** How the payer's opt-out route reads in the "How to switch" dialog. */
export const CARD_ROUTES: Record<string, { vendor: string; phone: string }> = {
  Humana: { vendor: 'ECHO', phone: '888-483-9212' },
  Principal: { vendor: 'ECHO', phone: '888-927-4394' },
  GEHA: { vendor: 'VPay', phone: '888-704-0096' },
};

// ---------------------------------------------------------------------------
// the rows
// ---------------------------------------------------------------------------

function row(r: Omit<PostedHistoryRow, 'dateLabel' | 'confirmedBy'> & { confirmedBy?: string }): PostedHistoryRow {
  return {
    ...r,
    dateLabel: longDate(r.dateIso),
    confirmedBy: r.confirmedBy ?? (CONFIRMERS[hash(r.id) % 4] ?? 'Ava Thompson (admin)'),
  };
}

function rowsFromDeposits(deposits: MatchedDeposit[]): PostedHistoryRow[] {
  const out: PostedHistoryRow[] = [];
  const first = (s: string): string => s.split(' ')[0] ?? s;
  for (const d of deposits) {
    for (const e of d.eras) {
      for (const c of e.claims) {
        out.push(
          row({
            id: `PH-${c.claimId ?? `${d.id}-${first(c.patient)}`}`,
            patient: c.patient,
            payer: d.payer,
            amount: c.paid,
            kind: 'Insurance payment',
            dateIso: e.postedIso || isoFromLabel(e.postedLabel),
            trace: e.trace,
            claimNumber: c.claimId ?? '',
            method: 'eft',
          }),
        );
      }
    }
    // Check deposits: each check posts its claims on the deposit day.
    for (const ch of d.checks) {
      const base = round2(ch.amount / ch.claimCount);
      for (let i = 0; i < ch.claimCount; i++) {
        const last = i === ch.claimCount - 1;
        const amount = last ? round2(ch.amount - base * (ch.claimCount - 1)) : base;
        out.push(
          row({
            id: `PH-${ch.checkNumber}-${String(i + 1)}`,
            patient: fullName(rng(hash(`${ch.checkNumber}|${String(i)}`))),
            payer: ch.payer,
            amount,
            kind: 'Insurance payment',
            dateIso: d.dateIso,
            trace: '',
            claimNumber: '',
            method: 'check',
            checkNo: ch.checkNumber,
          }),
        );
      }
    }
  }
  return out;
}

/** Posted claims behind the four deposits that don't match (they are posted, not yet matched). */
function rowsFromDontMatch(): PostedHistoryRow[] {
  return DM_POSTED.map((p, i) =>
    row({
      id: `PH-DM-${String(i + 1)}`,
      patient: p.patient,
      payer: p.payer,
      amount: p.amount,
      kind: 'Insurance payment',
      dateIso: p.iso,
      trace: p.trace,
      claimNumber: '',
      method: 'eft',
    }),
  );
}

function cardRows(): PostedHistoryRow[] {
  return CARD_SEED.map((c, i) =>
    row({
      id: `PH-CARD-${String(i + 1)}`,
      patient: c.patient,
      payer: c.payer,
      amount: c.amount,
      kind: 'Insurance payment',
      dateIso: c.iso,
      trace: '',
      claimNumber: '',
      method: 'card',
      fee: round2(c.amount * CARD_FEE_RATE),
      approval: String(100000 + (hash(`appr|${c.patient}`) % 899999)),
      last4: String(1000 + (hash(`l4|${c.patient}`) % 9000)),
    }),
  );
}

/** D271: payments someone posted in the PMS before ELVA did. ELVA linked them and didn't post again. */
const ALREADY_POSTED_SEED: { id: string; patient: string; payer: string; amount: number; iso: string; trace: string }[] = [
  { id: 'PH-LINK-1', patient: 'Priya Menon', payer: 'Delta Dental of Texas', amount: 176, iso: '2026-10-16', trace: '1T740593310' },
];
/** Rows added while the demo runs ("It's there" on a To-post question). */
const addedRows: PostedHistoryRow[] = [];

function alreadyPostedRows(): PostedHistoryRow[] {
  return [...addedRows, ...ALREADY_POSTED_SEED.map((a) => ({ ...a, dateIso: a.iso }))].map((a) =>
    row({
      id: a.id,
      patient: a.patient,
      payer: a.payer,
      amount: a.amount,
      kind: 'Insurance payment',
      dateIso: a.dateIso,
      trace: a.trace,
      claimNumber: '',
      method: 'eft',
      state: 'already_posted',
      confirmedBy: 'Posted by your team',
    }),
  );
}

/** "It's there": the payment was already in the PMS, so ELVA links it and it shows in Posted as Already posted. */
export function addAlreadyPostedRow(r: { id: string; patient: string; payer: string; amount: number; trace: string }): void {
  addedRows.unshift(row({
    id: `PH-LINK-${r.id}`, patient: r.patient, payer: r.payer, amount: r.amount, kind: 'Insurance payment',
    dateIso: POSTING_TODAY_ISO, trace: r.trace, claimNumber: '', method: 'eft', state: 'already_posted', confirmedBy: 'Posted by your team',
  }));
  cache = null;
}

/** The postings that left a patient credit (H1 §5). Each shows where the credit stands. */
function creditRows(): PostedHistoryRow[] {
  return creditSeeds().map((c) =>
    row({
      id: c.id,
      patient: c.patient,
      payer: c.payer,
      amount: c.amount,
      kind: 'Insurance payment',
      dateIso: c.postedIso,
      trace: c.payment.trace ?? '',
      claimNumber: '',
      method: c.payment.method === 'card' ? 'card' : c.payment.method === 'check' ? 'check' : 'eft',
      ...(c.payment.method === 'card'
        ? { fee: round2(c.amount * CARD_FEE_RATE), approval: c.payment.approval ?? '', last4: c.payment.last4 ?? '' }
        : {}),
      ...(c.payment.checkNo ? { checkNo: c.payment.checkNo } : {}),
      reference: referenceText(c.payment, c.payer, c.payment.trace ?? ''),
      credit: { state: c.state, ...(c.stateIso ? { stateIso: c.stateIso } : {}), sinceIso: c.postedIso, pronoun: c.pronoun },
    }),
  );
}

const OLD_END = '2026-06-30';
const OLD_SPAN = Math.round((Date.parse(`${OLD_END}T12:00:00Z`) - Date.parse(`${GO_LIVE_ISO}T12:00:00Z`)) / 86_400_000);

/** The older history (before the statements start): a smaller, steady stream. */
function olderRows(): PostedHistoryRow[] {
  return generate<PostedHistoryRow>(780, 0x9051, (r, i) => {
    const roll = r();
    const kind: PostedKind =
      roll < 0.8 ? 'Insurance payment'
        : roll < 0.9 ? 'Secondary payment'
          : roll < 0.955 ? 'Takeback'
            : roll < 0.985 ? 'Interest'
              : 'Refund';
    let amount: number;
    if (kind === 'Takeback' || kind === 'Refund') amount = -randMoney(r, 18, 640);
    else if (kind === 'Interest') amount = randMoney(r, 1, 22);
    else amount = randMoney(r, 42, 2400);
    const dateIso = addDays(GO_LIVE_ISO, Math.floor(r() * OLD_SPAN));
    return row({
      id: `PH-OLD-${String(i + 1)}`,
      patient: fullName(r),
      payer: pick(r, OLD_PAYERS),
      amount,
      kind,
      dateIso,
      trace: `1T${String(int(r, 1000000, 9999999))}`,
      claimNumber: String(int(r, 40000, 69999)),
      method: 'eft',
      confirmedBy: pick(r, CONFIRMERS),
    });
  });
}

let cache: PostedHistoryRow[] | null = null;

/** Newest first. Nothing is dated after the Posting "today". */
export function buildPostedHistory(): PostedHistoryRow[] {
  if (cache) return cache;
  const deposits = [...buildBaseDeposits(), ...buildFreshUploadDeposits(), ...buildNewAccountDeposits()];
  const rows = [...rowsFromDeposits(deposits), ...rowsFromDontMatch(), ...cardRows(), ...alreadyPostedRows(), ...creditRows(), ...olderRows()].filter(
    (r) => r.dateIso <= POSTING_TODAY_ISO,
  );
  cache = rows.sort((a, b) => (a.dateIso < b.dateIso ? 1 : a.dateIso > b.dateIso ? -1 : a.id < b.id ? -1 : 1));
  return cache;
}

/** Payers shown in the payer filter. */
export function historyPayers(rows: PostedHistoryRow[]): string[] {
  return [...new Set(rows.map((r) => r.payer))].sort((a, b) => a.localeCompare(b));
}

/** Payments posted from the first of the Posting "today" month (the Posted tab count). */
export function postedThisMonth(): { count: number; total: number } {
  const from = `${POSTING_TODAY_ISO.slice(0, 7)}-01`;
  // Already posted (D271): someone else posted it, so it isn't one ELVA posted this month.
  const rows = buildPostedHistory().filter((r) => r.dateIso >= from && r.dateIso <= POSTING_TODAY_ISO && r.state !== 'already_posted');
  return { count: rows.length, total: round2(rows.reduce((s, r) => s + r.amount, 0)) };
}

/** Payers that paid by card in the last 90 days, with what the card fees cost. */
export function cardPayers(dismissed: Record<string, string>): CardPayer[] {
  const since = addDays(POSTING_TODAY_ISO, -90);
  const byPayer = new Map<string, PostedHistoryRow[]>();
  for (const r of buildPostedHistory()) {
    if (r.method !== 'card' || r.dateIso < since) continue;
    byPayer.set(r.payer, [...(byPayer.get(r.payer) ?? []), r]);
  }
  const out: CardPayer[] = [];
  for (const [payer, rows] of byPayer) {
    const sorted = [...rows].sort((a, b) => (a.dateIso < b.dateIso ? -1 : 1));
    const asked = dismissed[payer];
    // A payer you asked stays hidden for 30 days after the ask; a later card payment brings the card back.
    const after = asked ? sorted.filter((r) => r.dateIso > addDays(asked, 30)) : [];
    if (asked && after.length === 0) continue;
    out.push({
      payer,
      count: sorted.length,
      fees: round2(sorted.reduce((s, r) => s + (r.fee ?? 0), 0)),
      firstIso: sorted[0]?.dateIso ?? since,
      lastIso: sorted[sorted.length - 1]?.dateIso ?? since,
      afterAsk: after.length,
    });
  }
  return out.sort((a, b) => b.count - a.count || a.payer.localeCompare(b.payer));
}
